<template>
  <div id="client-access" class="bst-page">
    <!-- Barre d'outils -->
    <div class="bst-toolbar">
      <div class="bst-toolbar-group">
        <vs-button size="small" icon-pack="feather" icon="icon-user-plus" @click="openCreate">New client account</vs-button>
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw" :disabled="loading" @click="load">Refresh</vs-button>
      </div>
      <div v-if="!loading && !loadError" class="bst-toolbar-group bst-count">
        <span class="bst-muted">{{ accounts.length }} client account(s) · {{ activeCount }} active</span>
      </div>
    </div>
    <p class="bst-hint">
      <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" />
      A client account is read-only and only sees the contacts you assign: their orders in progress (stock shown as "in stock / to prepare", truck planned), previous orders and invoices, with the PDFs. Never the internal note.
    </p>

    <!-- Création -->
    <vx-card v-if="createOpen" class="mb-base" title="New client account">
      <div class="ca-grid">
        <vs-input label="Username" v-model="draft.username" class="w-full" />
        <vs-input label="E-mail (sign-in and 2FA codes)" v-model="draft.email" class="w-full" />
        <div class="ca-password">
          <vs-input label="Password" v-model="draft.password" class="w-full" autocomplete="off" />
          <vs-button type="border" size="small" icon-pack="feather" icon="icon-shuffle" @click="draft.password = generatePassword()">Generate</vs-button>
        </div>
      </div>
      <div class="mt-4">
        <div class="ca-label">Contacts the client will see</div>
        <contact-picker v-model="draft.people" />
      </div>
      <vs-alert v-if="createError" color="danger" active="true" class="mt-4">{{ createError }}</vs-alert>
      <div class="ca-actions mt-4">
        <vs-button :disabled="creating" @click="create">Create</vs-button>
        <vs-button type="flat" @click="createOpen = false">Cancel</vs-button>
      </div>
    </vx-card>

    <!-- À transmettre au client après création -->
    <vs-alert v-if="created" color="success" active="true" class="mb-base">
      <strong>{{ created.username }}</strong> created. Send the client: the sign-in page {{ loginUrl }}, the e-mail
      <strong>{{ created.email }}</strong> and the password <code class="ca-code">{{ created.password }}</code>
      (shown once).
      <vs-button type="flat" size="small" color="success" @click="created = null">Dismiss</vs-button>
    </vs-alert>

    <div class="bst-content">
      <div v-if="loading" class="bst-empty">
        <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="bst-spin" />
        <div>Loading client accounts…</div>
      </div>
      <vs-alert v-else-if="loadError" color="danger" active="true" class="mb-4">{{ loadError }}</vs-alert>
      <div v-else-if="!accounts.length" class="bst-empty">
        <feather-icon icon="InboxIcon" svgClasses="h-8 w-8" />
        <div>No client account yet.</div>
      </div>

      <vx-card v-else class="bst-card" title="Client accounts">
        <div class="overflow-x-auto">
          <table class="bst-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Contacts visible</th>
                <th>Language</th>
                <th>2FA</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="a in accounts">
                <tr :key="a.iduser" :class="{ 'bst-off': !a.isactive }">
                  <td class="strong">
                    {{ a.username }}
                    <span v-for="r in a.other_roles" :key="r" class="bst-pill pill-warning ml-1"
                      title="This account has another role too: only the client portal is open to it unless that role is admin or moderator.">{{ r }}</span>
                    <div class="bst-sub">{{ a.email }}</div>
                  </td>
                  <td class="ca-wrap">
                    <span v-for="p in a.people" :key="p.id" class="bst-pill mr-1" :class="p.isactive ? 'pill-success' : 'pill-muted'">#{{ p.id }} {{ p.name }}</span>
                    <span v-if="!a.people.length" class="bst-pill pill-danger">none</span>
                  </td>
                  <td>{{ String(a.lang || '').toUpperCase() }}</td>
                  <td>
                    <span v-if="a.twofa_enabled" class="bst-pill pill-info">{{ a.twofa_method || 'on' }}</span>
                    <span v-else class="bst-muted">off</span>
                  </td>
                  <td>
                    <vs-switch color="success" :value="a.isactive" :disabled="!!busy[a.iduser]" @input="setActive(a, $event)" />
                  </td>
                  <td>
                    <div class="ca-actions">
                      <vs-button type="flat" size="small" icon-pack="feather" icon="icon-eye" @click="preview(a)">Preview</vs-button>
                      <vs-button type="flat" size="small" icon-pack="feather" icon="icon-users" @click="toggle(a, 'people')">Contacts</vs-button>
                      <vs-button type="flat" size="small" icon-pack="feather" icon="icon-key" @click="toggle(a, 'password')">Password</vs-button>
                      <vs-button v-if="a.twofa_enabled" type="flat" size="small" color="warning" icon-pack="feather" icon="icon-shield-off" :disabled="!!busy[a.iduser]" @click="resetTwofa(a)">Reset 2FA</vs-button>
                      <vs-button type="flat" size="small" icon-pack="feather" icon="icon-list" @click="toggle(a, 'history')">History</vs-button>
                    </div>
                  </td>
                </tr>
                <tr v-if="panel.iduser === a.iduser" :key="a.iduser + '-panel'" class="bst-audit">
                  <td colspan="6">
                    <div v-if="panel.kind === 'people'" class="ca-panel">
                      <contact-picker v-model="panel.people" />
                      <div class="ca-actions mt-3">
                        <vs-button size="small" :disabled="!!busy[a.iduser]" @click="savePeople(a)">Save contacts</vs-button>
                        <vs-button size="small" type="flat" @click="closePanel">Cancel</vs-button>
                      </div>
                    </div>
                    <div v-else-if="panel.kind === 'password'" class="ca-panel ca-password">
                      <vs-input label="New password" v-model="panel.password" class="w-full" autocomplete="off" />
                      <vs-button type="border" size="small" icon-pack="feather" icon="icon-shuffle" @click="panel.password = generatePassword()">Generate</vs-button>
                      <vs-button size="small" :disabled="!!busy[a.iduser] || !panel.password" @click="savePassword(a)">Set password</vs-button>
                      <vs-button size="small" type="flat" @click="closePanel">Cancel</vs-button>
                    </div>
                    <audit-log-table v-else entity="user" :id="a.iduser" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
// Restricted Area → Client Access : logins du portail client (rôle « client »,
// mig 105) et fiches visibles par chacun (user_people). API admin seulement
// (clientportal.routes.js) ; le serveur confine le client à /api/portal/*.
import axios from '@/axios.js'
import AuditLogTable from '@/components/AuditLogTable.vue'
import ContactPicker from '@/views/client/admin/ContactPicker.vue'

function errorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  return (data && data.message) || fallback
}

// 16 caractères aléatoires (score zxcvbn maximal, règles NIST du serveur).
const ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
function randomPassword () {
  const bytes = new Uint32Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, b => ALPHABET[b % ALPHABET.length]).join('')
}

function emptyDraft () {
  return { username: '', email: '', password: randomPassword(), people: [] }
}

export default {
  name: 'ClientAccess',
  components: { AuditLogTable, ContactPicker },
  data () {
    return {
      loading: true,
      loadError: null,
      accounts: [],
      busy: {},
      createOpen: false,
      creating: false,
      createError: null,
      created: null,
      draft: emptyDraft(),
      panel: { iduser: null, kind: null, people: [], password: '' }
    }
  },
  computed: {
    activeCount () { return this.accounts.filter(a => a.isactive).length },
    loginUrl () {
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      return `${window.location.origin}${base}pages/login`
    }
  },
  methods: {
    generatePassword: randomPassword,
    async load () {
      this.loading = true
      this.loadError = null
      try {
        const res = await axios.get('/clientaccess')
        this.accounts = res.data.accounts || []
      } catch (err) {
        this.loadError = errorMessage(err, 'Could not load the client accounts.')
      } finally {
        this.loading = false
      }
    },
    openCreate () {
      this.draft = emptyDraft()
      this.createError = null
      this.createOpen = true
    },
    async create () {
      this.createError = null
      this.creating = true
      const password = this.draft.password
      try {
        const res = await axios.post('/clientaccess', {
          username: this.draft.username,
          email: this.draft.email,
          password,
          people: this.draft.people.map(p => p.id)
        })
        this.accounts.push(res.data)
        this.created = { username: res.data.username, email: res.data.email, password }
        this.createOpen = false
      } catch (err) {
        this.createError = errorMessage(err, 'Could not create the client account.')
      } finally {
        this.creating = false
      }
    },
    replace (account) {
      const i = this.accounts.findIndex(a => a.iduser === account.iduser)
      if (i !== -1) this.accounts.splice(i, 1, account)
    },
    async update (a, body, okText) {
      this.$set(this.busy, a.iduser, true)
      try {
        const res = await axios.put(`/clientaccess/${a.iduser}`, body)
        this.replace(res.data)
        this.notify('success', 'Saved', okText)
        return true
      } catch (err) {
        this.notify('danger', 'Denied', errorMessage(err, 'Could not update the client account.'))
        return false
      } finally {
        this.$set(this.busy, a.iduser, false)
      }
    },
    async setActive (a, value) {
      const on = !!value
      if (on === a.isactive) return
      const ok = await this.update(a, { isactive: on }, on ? `${a.username} can sign in again.` : `${a.username} is signed out and can no longer sign in.`)
      if (!ok) this.replace(Object.assign({}, a))
    },
    toggle (a, kind) {
      if (this.panel.iduser === a.iduser && this.panel.kind === kind) return this.closePanel()
      this.panel = { iduser: a.iduser, kind, people: a.people.slice(), password: kind === 'password' ? randomPassword() : '' }
    },
    closePanel () {
      this.panel = { iduser: null, kind: null, people: [], password: '' }
    },
    async savePeople (a) {
      if (await this.update(a, { people: this.panel.people.map(p => p.id) }, `Contacts of ${a.username} updated.`)) this.closePanel()
    },
    async savePassword (a) {
      const password = this.panel.password
      if (await this.update(a, { password }, `New password for ${a.username}: ${password} (send it to the client).`)) this.closePanel()
    },
    async resetTwofa (a) {
      this.$set(this.busy, a.iduser, true)
      try {
        await axios.post(`/auth/2fa/admin/reset/${a.iduser}`)
        this.replace(Object.assign({}, a, { twofa_enabled: false, twofa_method: null }))
        this.notify('success', '2FA reset', `${a.username} will set up two-factor authentication again at next sign-in.`)
      } catch (err) {
        this.notify('danger', 'Denied', errorMessage(err, 'Could not reset the 2FA.'))
      } finally {
        this.$set(this.busy, a.iduser, false)
      }
    },
    preview (a) {
      const route = this.$router.resolve({ name: 'client-orders', query: { as: a.iduser } })
      window.open(route.href, '_blank')
    },
    notify (color, title, text) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'success' ? 'icon-check' : 'icon-alert-circle', position: 'bottom-right', time: 10000 })
    }
  },
  created () {
    if (!(localStorage.getItem('login-roles') || '').toLowerCase().includes('admin')) {
      this.notify('danger', 'Denied', 'Redirection')
      this.$router.push({ path: '/' })
      return
    }
    this.load()
  }
}
</script>

<style scoped>
/* Même habillage que Broker Check → Broker Status (BrokerStatus.vue). */
.bst-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.06);
}
.bst-toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}
.bst-count {
  font-size: 0.8125rem;
}
.bst-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 4px 18px;
  color: #64748b;
  font-size: 0.8125rem;
}
.bst-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 3rem 1rem;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  text-align: center;
}
.bst-spin {
  animation: bst-spin 1s linear infinite;
}
@keyframes bst-spin {
  to {
    transform: rotate(360deg);
  }
}
.bst-muted {
  color: #94a3b8;
}
.bst-sub {
  color: #94a3b8;
  font-weight: 400;
  font-size: 0.6875rem;
}
.bst-pill {
  display: inline-block;
  margin-bottom: 2px;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
.pill-warning { background: #fff3e0; color: #e65100; }
.pill-success { background: #e8f5e9; color: #2e7d32; }
.pill-danger { background: #ffebee; color: #c62828; }
.pill-info { background: #e3f2fd; color: #1565c0; }
.pill-muted { background: #f1f5f9; color: #64748b; }
.bst-card .overflow-x-auto {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.bst-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  white-space: nowrap;
}
.bst-table th {
  padding: 8px 8px;
  background: #455a64;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: left;
  vertical-align: bottom;
}
.bst-table td {
  padding: 8px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  vertical-align: middle;
}
.bst-table tbody tr:nth-child(even) td { background: #f8fafc; }
.bst-table .strong { font-weight: 700; }
.bst-table tr.bst-off td { background: #fafafa; color: #94a3b8; }
.bst-table tr.bst-audit td { white-space: normal; background: #fff; }
.ca-wrap { white-space: normal; max-width: 420px; }
.ca-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.ca-password {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}
.ca-password .vs-input { flex: 1 1 220px; }
.ca-label {
  margin-bottom: 6px;
  color: #64748b;
  font-size: 0.8125rem;
}
.ca-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.ca-panel { padding: 6px 2px; }
.ca-code {
  padding: 1px 6px;
  border-radius: 4px;
  background: #fff;
  font-family: monospace;
}
</style>
